// Tranche AH pass 1: outbox job detail page.
//
// API GAP (filed): there is no `GET /admin/outbox/:id` endpoint — only
// `GET /admin/outbox/jobs` (list, with optional filters) and the
// retry/delete mutations. The detail page filters the list response
// client-side for now; when the API grows a by-id route, swap the
// loader.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  ControllerIdsPanel,
  DangerousActionButton,
  EntityDetailLayout,
  Icon,
  Spinner,
  type AuditEntry,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { fetchAuditForResource } from '../lib/detailFetchers.js';

type JobState = 'pending' | 'running' | 'succeeded' | 'failed' | 'dead_letter';

interface OutboxJobRow {
  id: string;
  type: string;
  tenant_id: string;
  resource_type: string;
  resource_id: string;
  state: JobState;
  attempt_count: number;
  max_attempts: number;
  next_run_at: string;
  last_error: string | null;
  idempotency_key?: string | null;
  controller_object_id?: string | null;
  scheduled_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  payload?: unknown;
  controller_result_json?: unknown;
}

const STATE_TONE: Record<JobState, string> = {
  pending: 'bg-tertiary-fixed text-on-tertiary-fixed',
  running: 'bg-primary-container text-on-primary',
  succeeded: 'bg-secondary-container text-on-secondary-container',
  failed: 'bg-error-container text-on-error-container',
  dead_letter: 'bg-error-container text-on-error-container',
};

export function OutboxJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<OutboxJobRow | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setJob(null);
    setError(null);
    void (async () => {
      try {
        // TODO(api-gap): no GET /admin/outbox/:id; we pull a recent page
        // and find by id. Adequate for triage; a dedicated endpoint would
        // let us drop the list cap.
        const list = await apiClient.get<{ data: OutboxJobRow[] }>(
          '/api/v1/admin/outbox/jobs?limit=200',
          { skipTenantScope: true },
        );
        const row = list.data.find((j) => j.id === id);
        if (!row) {
          if (!cancelled) setError('Outbox job not found — it may have been deleted.');
          return;
        }
        const a = await fetchAuditForResource('outbox_job', row.id, 10);
        if (cancelled) return;
        setJob(row);
        setAudit(a);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load outbox job');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick]);

  const reload = useCallback(() => {
    setReloadTick((tick) => tick + 1);
  }, []);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/outbox')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Outbox
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const badges = (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATE_TONE[job.state]}`}
      data-testid="outbox-state-chip"
    >
      {job.state}
    </span>
  );

  const safeActions = (
    <DangerousActionButton
      actionLabel="Retry…"
      title={`Retry ${job.type}?`}
      description="Resets the job to pending and re-runs immediately. Idempotency-key replay collapses duplicates."
      confirmLabel="Retry"
      testId="outbox-retry"
      // Retry needs a reason per existing outbox endpoint; we use the
      // DangerousActionButton wrapper to keep the audit story consistent
      // with the operator-confirm pattern, even though retry itself is
      // safe to repeat.
      onConfirm={async (reason, intentKey) => {
        // UX-013: the key used to be minted here, so retrying inside the same
        // dialog — the common path, since a failure keeps it open — sent a new
        // Idempotency-Key and the backend could not collapse the duplicate.
        const key = intentKey;
        await apiClient
          .withIdempotencyKey(key)
          .post(`/api/v1/admin/outbox/${job.id}/retry`, { reason }, {
            skipTenantScope: true,
          });
        reload();
      }}
    />
  );

  const dangerousActions = (
    <DangerousActionButton
      actionLabel="Delete…"
      title={`Delete ${job.type}?`}
      description="Drops the job from the queue. Use this only after the controller has been manually reconciled."
      testId="outbox-delete"
      onConfirm={async (reason, intentKey) => {
        // UX-013: the key used to be minted here, so retrying inside the same
        // dialog — the common path, since a failure keeps it open — sent a new
        // Idempotency-Key and the backend could not collapse the duplicate.
        const key = intentKey;
        await apiClient
          .withIdempotencyKey(key)
          .deleteWithBody(`/api/v1/admin/outbox/${job.id}`, { reason }, {
            skipTenantScope: true,
          });
        navigate('/outbox');
      }}
    />
  );

  return (
    <EntityDetailLayout
      title={job.type}
      subtitle={`${job.resource_type}:${job.resource_id.slice(0, 8)}`}
      badges={badges}
      meta={`Attempts ${job.attempt_count}/${job.max_attempts}`}
      backHref="/outbox"
      backLabel="Outbox"
      safeActions={safeActions}
      dangerousActions={dangerousActions}
      related={
        <ControllerIdsPanel
          ids={[
            { label: 'Idempotency key', value: job.idempotency_key ?? null },
            {
              label: 'Controller object id',
              value: job.controller_object_id ?? null,
              hint: 'Set when a committed-but-unfinalized side effect is reconciled.',
            },
          ]}
        />
      }
      audit={<AuditFeed entries={audit} label="Recent activity" />}
    >
      <section
        aria-label="Lifecycle"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="Kind" v={<code className="font-code text-code">{job.type}</code>} />
        <KV k="State" v={job.state} />
        <KV
          k="Scheduled at"
          v={job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : '—'}
        />
        <KV
          k="Started at"
          v={job.started_at ? new Date(job.started_at).toLocaleString() : '—'}
        />
        <KV
          k="Completed at"
          v={job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}
        />
        <KV k="Next run" v={new Date(job.next_run_at).toLocaleString()} />
      </section>

      <section
        aria-label="Payload"
        className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-3"
      >
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">
          Payload (redacted)
        </h2>
        <pre className="font-code text-code bg-surface-container rounded p-3 overflow-x-auto">
          {JSON.stringify(job.payload ?? {}, null, 2)}
        </pre>
      </section>

      <section
        aria-label="Controller response"
        className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-3"
      >
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">
          Controller response (redacted)
        </h2>
        <pre className="font-code text-code bg-surface-container rounded p-3 overflow-x-auto">
          {job.controller_result_json
            ? JSON.stringify(job.controller_result_json, null, 2)
            : '— (no response yet)'}
        </pre>
        {job.last_error ? (
          <>
            <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">
              Last error
            </h2>
            <pre className="font-code text-code bg-error-container text-on-error-container rounded p-3 overflow-x-auto">
              {job.last_error}
            </pre>
          </>
        ) : null}
      </section>
    </EntityDetailLayout>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
